import React, { useState } from "react";
import NoteEditor from "./NoteViewer";
import Header from "../components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import { Save, ArrowLeft, FileText } from "lucide-react";
import { useCreateNote } from "./hooks/useNote";
import { EMPTY_EDITOR_STATE } from "./noteEditorDefaults";
import Prism from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import "prismjs/components/prism-java";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-go";
import "prismjs/components/prism-kotlin";
import "prismjs/themes/prism-tomorrow.css";

const FENCED_CODE_BLOCK_REGEX = /```([a-zA-Z0-9_-]+)?[ \t]*\n([\s\S]*?)```/g;
const SUPPORTED_FENCE_LANGUAGES = new Set(['go', 'kotlin', 'javascript', 'python', 'java', 'sql']);

function normalizeFenceLanguage(language) {
    const normalized = String(language ?? '').trim().toLowerCase();

    switch (normalized) {
        case 'js':
            return 'javascript';
        case 'py':
            return 'python';
        case 'golang':
            return 'go';
        case 'kt':
            return 'kotlin';
        default:
            return normalized || 'plain';
    }
}

function extractTextWithBreaks(node) {
    if (!node) return '';
    if (node.type === 'linebreak') return '\n';
    if (node.type === 'text') return node.text ?? '';

    const childrenText = (node.children ?? []).map(extractTextWithBreaks).join('');
    if (['paragraph', 'heading', 'listitem', 'quote', 'code'].includes(node.type)) {
        return `${childrenText}\n`;
    }

    return childrenText;
}

function renderPreviewTextBlocks(text, keyPrefix) {
    return text
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((chunk, index) => (
            <p key={`${keyPrefix}-${index}`} className="text-[var(--text-primary)] text-sm leading-relaxed whitespace-pre-wrap">
                {chunk}
            </p>
        ));
}

function containsFencedCodeBlocks(value) {
    if (!value) return false;
    FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
    return FENCED_CODE_BLOCK_REGEX.test(value);
}

function renderFencedCodeContent(rawText) {
    const text = String(rawText ?? '');
    if (!text.trim()) {
        return <p className="text-xs text-[var(--text-muted)]">Live preview appears here as you type.</p>;
    }

    const blocks = [];
    let cursor = 0;
    FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
    let match = FENCED_CODE_BLOCK_REGEX.exec(text);

    while (match) {
        const [fullMatch, language, code] = match;
        const matchStart = match.index;
        const before = text.slice(cursor, matchStart);

        if (before.trim()) {
            blocks.push(...renderPreviewTextBlocks(before, `text-before-${matchStart}`));
        }

        const normalizedLanguage = normalizeFenceLanguage(language);
        const activeLanguage = SUPPORTED_FENCE_LANGUAGES.has(normalizedLanguage) ? normalizedLanguage : 'plain';
        const highlightedCode = activeLanguage === 'plain'
            ? Prism.util.encode(code)
            : Prism.highlight(code, Prism.languages[activeLanguage] ?? Prism.languages.javascript, activeLanguage);

        blocks.push(
            <pre key={`code-${matchStart}`} className="bg-[var(--surface-deep)] border border-[var(--border-color)] rounded-lg p-3 text-xs text-[var(--brand-coral)] font-mono overflow-x-auto my-1">
                <code
                    className={`language-${activeLanguage}`}
                    dangerouslySetInnerHTML={{__html: highlightedCode}}
                />
            </pre>
        );

        cursor = matchStart + fullMatch.length;
        match = FENCED_CODE_BLOCK_REGEX.exec(text);
    }

    const tail = text.slice(cursor);
    if (tail.trim()) {
        blocks.push(...renderPreviewTextBlocks(tail, 'text-tail'));
    }

    return blocks.length > 0
        ? <div className="flex flex-col gap-3">{blocks}</div>
        : <p className="text-xs text-[var(--text-muted)]">Write something to see a rendered preview.</p>;
}

function getAlignment(format) {
    switch (format) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        case 'justify': return 'text-justify';
        default: return '';
    }
}

function renderLexicalNode(node, index) {
    if (!node) return null;

    switch (node.type) {
        case 'root':
            return (
                <div key={index} className="flex flex-col gap-3">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </div>
            );
        case 'paragraph':
            return (
                <p key={index} className={`text-[var(--text-primary)] text-sm leading-relaxed whitespace-pre-wrap ${getAlignment(node.format)}`}>
                    {node.children?.length ? node.children.map((child, i) => renderLexicalNode(child, i)) : <br />}
                </p>
            );
        case 'heading': {
            const sizeMap = { h1: 'text-xl', h2: 'text-lg', h3: 'text-base', h4: 'text-sm', h5: 'text-xs', h6: 'text-xs' };
            const Tag = node.tag ?? 'h2';
            return (
                <Tag key={index} className={`font-bold text-[var(--text-primary)] ${sizeMap[Tag] ?? 'text-lg'} ${getAlignment(node.format)}`}>
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </Tag>
            );
        }
        case 'text': {
            const content = node.text;
            if (!content) return null;
            const fmt = node.format ?? 0;
            const isBold = fmt & 1;
            const isItalic = fmt & 2;
            const isUnder = fmt & 8;
            const isStrike = fmt & 4;
            const isCode = fmt & 16;
            if (isCode) return <code key={index} className="bg-[var(--surface-deep)] text-[var(--brand-coral)] rounded px-1 py-0.5 text-xs font-mono">{content}</code>;
            let el = <span key={index}>{content}</span>;
            if (isBold) el = <strong key={index} className="font-semibold text-[var(--text-primary)]">{content}</strong>;
            if (isItalic) el = <em key={index} className="italic">{content}</em>;
            if (isUnder) el = <u key={index}>{content}</u>;
            if (isStrike) el = <s key={index} className="line-through text-[var(--text-muted)]">{content}</s>;
            return el;
        }
        case 'link':
            return (
                <a key={index} href={node.url} target="_blank" rel="noopener noreferrer" className="text-[var(--brand-coral)] underline hover:text-[var(--brand-coral)] transition">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </a>
            );
        case 'list':
            return node.listType === 'bullet'
                ? <ul key={index} className="list-disc list-inside flex flex-col gap-1 text-[var(--text-primary)]">{node.children?.map((child, i) => renderLexicalNode(child, i))}</ul>
                : <ol key={index} className="list-decimal list-inside flex flex-col gap-1 text-[var(--text-primary)]">{node.children?.map((child, i) => renderLexicalNode(child, i))}</ol>;
        case 'listitem':
            return (
                <li key={index} className="text-[var(--text-primary)] leading-relaxed">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </li>
            );
        case 'quote':
            return (
                <blockquote key={index} className="border-l-4 border-[var(--brand-coral)]/50 pl-4 text-[var(--text-muted)] italic my-1">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </blockquote>
            );
        case 'code':
            return (
                <pre key={index} className="bg-[var(--surface-deep)] border border-[var(--border-color)] rounded-lg p-3 text-xs text-[var(--brand-coral)] font-mono overflow-x-auto my-1">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </pre>
            );
        case 'linebreak':
            return <br key={index} />;
        default:
            return node.children ? <span key={index}>{node.children.map((child, i) => renderLexicalNode(child, i))}</span> : null;
    }
}

function renderLiveNotePreview(raw) {
    if (!raw) return <p className="text-xs text-[var(--text-muted)]">Live preview appears here as you type.</p>;

    try {
        const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const root = json?.root ?? json;
        const plainText = extractTextWithBreaks(root).trim();
        if (containsFencedCodeBlocks(plainText)) {
            return renderFencedCodeContent(plainText);
        }
        return renderLexicalNode(root, 0);
    } catch {
        if (containsFencedCodeBlocks(raw)) {
            return renderFencedCodeContent(raw);
        }
        return <p className="text-[var(--text-primary)] text-sm leading-relaxed whitespace-pre-wrap">{String(raw)}</p>;
    }
}

function Note() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { authToken } = useAuth();
    const { mutateAsync: createNote } = useCreateNote(authToken);
    const [title, setTitle] = useState("");
    const [tags, setTags] = useState("");
    const [content, setContent] = useState(EMPTY_EDITOR_STATE);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const activeFolder = searchParams.get("folder");
    const activeProject = searchParams.get("project");
    const notesPath = activeProject
        ? `/project/${activeProject}`
        : (activeFolder ? `/notes?folder=${activeFolder}` : "/notes");
    const backLabel = activeProject ? "Back to project" : "Back to notes";
    const saveLabel = saving ? "Saving..." : saved ? "Saved ✓" : "Save";

    const handleSave = async () => {
        setSaving(true);
        try {
            const createdNote = await createNote({
                tags,
                title,
                content,
                parentFolderId: activeFolder ?? null,
                projectId: activeProject ?? null,
            });

            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            navigate(`/note/${createdNote.id}`);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)]">
            <Header />

            <div className="max-w-4xl mx-auto px-6 pt-24 pb-20">

                {/* Top bar */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => navigate(notesPath)}
                        className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition"
                    >
                        <ArrowLeft size={16} />
                        {backLabel}
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                            saved
                                ? "bg-green-600/20 border border-green-500/30 text-green-400"
                                : "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)] shadow-lg hover:brightness-105"
                        } disabled:opacity-60`}
                    >
                        <Save size={15} />
                        {saveLabel}
                    </button>
                </div>

                {/* Document card */}
                <div className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] shadow-[var(--menu-shadow)]">

                    {/* Doc header */}
                    <div className="px-12 pt-10 pb-6 border-b border-[var(--border-color)]">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-[var(--brand-coral)]/10 border border-[var(--brand-coral)]/20 flex items-center justify-center flex-shrink-0">
                                <FileText size={15} className="text-[var(--brand-coral)]" />
                            </div>
                            <span className="text-xs text-[var(--text-muted)] uppercase tracking-widest">New Note</span>
                        </div>

                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Add Title..."
                            className="w-full bg-transparent text-4xl font-bold text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none caret-[var(--brand-coral)] leading-tight"
                        />

                        <input
                            type="text"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="Add tags... (#cs, #backend)"
                            className="mt-4 w-full bg-transparent text-sm text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] outline-none caret-[var(--brand-coral)]"
                        />
                    </div>

                    {/* Editor */}
                    <div className="px-0">
                        <NoteEditor initialEditorState={content} onChange={setContent} />
                    </div>

                    <div className="mx-12 mb-8 rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">Live preview</p>
                        {renderLiveNotePreview(content)}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Note;
